/**
 * SCRUM-1032 — what a station is still missing.
 *
 * One rule, two readers. The Save button paints these fields red and refuses;
 * the guided setup's autosave uses the same list to decide the opposite way —
 * a row with anything missing is simply passed over, silently, because the user
 * is still filling it in and a complaint every few seconds is not help.
 *
 * The API has no draft state: a station either exists with every required field
 * or does not exist at all. So "ready" has to be decided here, on the client,
 * before anything is written.
 */

/** `stationType` 1. Non-irrigation stations carry no agronomy or flow data. */
const NON_IRRIGATION = 1

const missingText = (value: unknown) => !value || value === '-'

/** Only the two real types count; a stored 0 or a blank is no type at all. */
const missingStationType = (value: unknown) => ![1, 2].includes(Number(value))

/** 0 and empty both mean "not set" on these; a real value is positive. */
const missingNumber = (value: unknown) =>
  value === null || value === undefined || value === '' || !(Number(value) > 0)

const hasPipe = (station: any) =>
  Boolean(
    station.pipeId ||
      (typeof station.pipe === 'object' && station.pipe?.id) ||
      (typeof station.pipe === 'string' &&
        station.pipe.trim() !== '' &&
        station.pipe !== '-'),
  )

/**
 * The required fields this station has not answered, in the order they appear
 * in the grid. Empty means the row can be written.
 */
export const missingStationFields = (station: any): string[] => {
  const missing: string[] = []
  const add = (field: string, invalid: boolean) => {
    if (invalid) missing.push(field)
  }

  // Required for every station. A blank name is caught here rather than left to
  // the server, which quietly substitutes the decoder's name for an empty one —
  // the row would come back renamed with no indication why.
  add('description', missingText(station.description))
  add('decoderAddress', missingText(station.decoderAddress))
  // Mandatory: without it the save would fall back to Irrigation on its own,
  // which is a choice the user never made.
  add('stationType', missingStationType(station.stationType))
  add('area', missingText(station.area))
  add('subArea', !station.subAreaId)

  // Irrigation stations additionally require the agronomic and flow fields, on
  // every save and not just when the row is new.
  if (Number(station.stationType) !== NON_IRRIGATION) {
    add('plant', !station.plantId)
    add('soil', !station.soilId)
    add('emitter', !station.emitterId)
    add('pipe', !hasPipe(station))
    add('expectedFlow', missingNumber(station.expectedFlow))
    add('precipitationRate', missingNumber(station.precipitationRate))
    add('priority', missingNumber(station.priority))
  }

  return missing
}

/** Whether this row carries everything the create endpoint requires. */
export const isStationComplete = (station: any): boolean =>
  missingStationFields(station).length === 0

/** Column names as the user reads them, for saying what a row still needs. */
const FIELD_LABELS: Record<string, string> = {
  description: 'Station Name',
  decoderAddress: 'Decoder Address',
  stationType: 'Type',
  area: 'Area',
  subArea: 'Sub Area',
  plant: 'Plant',
  soil: 'Soil',
  emitter: 'Emitter',
  pipe: 'Pipe',
  expectedFlow: 'Expected Flow',
  precipitationRate: 'Precipitation Rate',
  priority: 'Priority',
}

/**
 * What is holding the unsaved rows back, most common first.
 *
 * The autosave passes over an incomplete row without a word, which is right
 * while someone is typing and wrong once they have filled a column and are
 * waiting for something to happen. Filling one column of a thousand rows
 * leaves every one of them short of six other fields, and a count that does
 * not move looks exactly like a broken save.
 */
export const describeBlockedRows = (
  rows: any[],
): { count: number; fields: string[] } => {
  const counts = new Map<string, number>()
  let blocked = 0

  rows.forEach((row) => {
    const missing = missingStationFields(row)
    if (missing.length === 0) return
    blocked += 1
    missing.forEach((field) => counts.set(field, (counts.get(field) ?? 0) + 1))
  })

  const fields = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([field]) => FIELD_LABELS[field] ?? field)

  return { count: blocked, fields }
}

/**
 * The required fields this station is missing, as the user reads them.
 *
 * SCRUM-1032 QC round 7: an edit that empties a required field on a saved
 * station is not written (it would store a blank). It used to be skipped
 * without a word; the row now says which fields it needs.
 */
export const missingFieldLabels = (station: any): string[] =>
  missingStationFields(station).map((field) => FIELD_LABELS[field] ?? field)
